import React, { FC, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConfiguration } from '../../context/ConfigurationContext';
import { useAuth } from '../../context/AuthContext';
import { useHasSenior } from '../../hooks/useHasSenior';
import { LanguageToggle } from '../../components/ui/LanguageToggle';
import type { ProfileType } from '../../types';

// Import all step components
import PreScreening from './PreScreening';
import Struggles from './Struggles';
import LayoutSelection from './LayoutSelection';
import ContactSetup from './ContactSetup';
import FeatureSetup from './FeatureSetup';
import DataAdvisory from './DataAdvisory';
import ReviewConfirm from './ReviewConfirm';
import Deployment from './Deployment';
import ConfigurationComplete from './ConfigurationComplete';

const STEPS = [
  'prescreening', // Step 1: Pre-Screening Questions (P1-P4)
  'struggles',    // Step 2: Q5 Struggles (replaces Profile Selection + Assessment)
  'layout',       // Step 3: Layout Selection
  'contacts',     // Step 4: Contact Setup
  'features',     // Step 5: Feature Setup
  'dataadvisory', // Step 6: Mobile Data Advisory
  'review',       // Step 7: Review & Confirm
  'deployment',   // Step 8: Deployment Progress
  'complete',     // Step 9: Complete
] as const;

type Step = typeof STEPS[number];

const ConfigurationWizard: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSelectedProfile, setSelectedPlan, selectedPlan, resetConfiguration } = useConfiguration();
  const { user, loading: authLoading } = useAuth();
  const { hasSenior, loading: seniorLoading } = useHasSenior();

  const [currentStep, setCurrentStep] = useState<Step>('prescreening');

  // B2C guard: if an authenticated caregiver already has a senior,
  // the configure wizard is off-limits — redirect to the dashboard.
  // Skip while in the deployment/complete steps: the senior is created during
  // deployment, and we need to redirect to Stripe (not /dashboard) right after.
  useEffect(() => {
    if (authLoading || seniorLoading) return;
    if (currentStep === 'deployment' || currentStep === 'complete') return;
    if (user && hasSenior) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, seniorLoading, user, hasSenior, currentStep, navigate]);

  // Handle profile from URL query param (from landing page)
  useEffect(() => {
    const profileFromUrl = searchParams.get('profile') as ProfileType | null;
    if (profileFromUrl && ['cognitive', 'physical', 'visual', 'custom'].includes(profileFromUrl)) {
      setSelectedProfile(profileFromUrl);
      // Skip prescreening and struggles since profile is pre-selected
      setCurrentStep('layout');
    }
  }, [searchParams, setSelectedProfile]);

  // Auto-select the only available plan
  useEffect(() => {
    if (!selectedPlan) {
      setSelectedPlan('plus');
    }
  }, [selectedPlan, setSelectedPlan]);

  const handleNext = () => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1]);
    } else {
      navigate('/');
    }
  };

  const handleComplete = () => {
    navigate('/dashboard');
  };

  const handleStartOver = () => {
    resetConfiguration();
    setCurrentStep('prescreening');
  };

  const handleEditStep = (step: string) => {
    // Map old step names to new ones for backward compatibility
    const stepMap: Record<string, Step> = {
      prescreening: 'prescreening',
      profile: 'struggles',
      assessment: 'struggles',
      struggles: 'struggles',
      layout: 'layout',
      contacts: 'contacts',
      features: 'features',
      review: 'review',
    };
    const mappedStep = stepMap[step];
    if (mappedStep) {
      setCurrentStep(mappedStep);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'prescreening':
        return <PreScreening onNext={handleNext} onBack={() => navigate('/')} />;
      case 'struggles':
        return <Struggles onNext={handleNext} onBack={handleBack} />;
      case 'layout':
        return <LayoutSelection onNext={handleNext} onBack={handleBack} />;
      case 'contacts':
        return <ContactSetup onNext={handleNext} onBack={handleBack} />;
      case 'features':
        return <FeatureSetup onNext={handleNext} onBack={handleBack} />;
      case 'dataadvisory':
        return <DataAdvisory onNext={handleNext} onBack={handleBack} />;
      case 'review':
        return (
          <ReviewConfirm
            onNext={handleNext}
            onBack={handleBack}
            onEditStep={handleEditStep}
          />
        );
      case 'deployment':
        return <Deployment onComplete={handleNext} />;
      case 'complete':
        return (
          <ConfigurationComplete
            onFinish={handleComplete}
            onStartOver={handleStartOver}
          />
        );
      default:
        return <PreScreening onNext={handleNext} onBack={() => navigate('/')} />;
    }
  };

  // While we're resolving auth + existing-senior state, show a spinner
  // so we don't flash the pre-screening step before redirecting.
  // Skip on the deployment/complete steps — those manage their own loading
  // state in-place, and remounting them here would wipe their progress
  // (the form would briefly re-render empty while signUp triggers re-resolution).
  const shouldShowGlobalSpinner =
    (authLoading || seniorLoading) &&
    currentStep !== 'deployment' &&
    currentStep !== 'complete';
  if (shouldShowGlobalSpinner) {
    return (
      <div className="min-h-screen bg-brand-bg dark:bg-slate-900 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      {renderStep()}
      {currentStep !== 'deployment' && currentStep !== 'complete' && (
        <div className="fixed bottom-4 right-4 z-40 bg-white/95 dark:bg-slate-800/95 backdrop-blur rounded-full shadow-lg border border-brand-border dark:border-slate-700 p-1">
          <LanguageToggle />
        </div>
      )}
    </>
  );
};

export default ConfigurationWizard;
